/**
 * A party in the browse list.
 *
 * Mostly artwork and a dark surface: the game's own banner does the work of
 * making the card attractive, so the card itself stays quiet. Two tags at
 * most over the image, then the facts you scan before deciding, then one
 * button.
 *
 * The selected card takes a violet border and the faintest glow - enough to
 * tie it to the details panel that just opened beside it, not enough to
 * shout across the grid.
 */

import { useRef, useState } from 'react';
import type { Party } from '@/lib/types';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';
import { gameBanner, gameIconArt } from '@/lib/gameArt';
import { WaitButton } from './PartyExtras';
import { reasonsFor } from '@/lib/match';

/** Short names for the platforms, for a chip. */
const PLATFORM_LABEL: Record<string, string> = {
  pc: 'PC', playstation: 'PlayStation', xbox: 'Xbox', switch: 'Switch', mobile: 'Mobile'
};
import { useSession } from '@/stores/session';
import { Tooltip } from './Tooltip';
import { Avatar } from './Avatar';
import { useHoverCard } from './HoverCard';
import { useContextMenu, type MenuItem } from './Menu';
import { PartyHoverBody } from './PartyHover';
import { EmojiText } from './EmojiText';

type Props = {
  party: Party;
  /**
   * The phone layout: a row rather than a tile. A 142px banner over every
   * card means two parties per screen, so on a phone the artwork shrinks to
   * a square beside the text and six fit instead.
   */
  compact?: boolean;
  /** The list view: one slim line per party instead of a card. */
  row?: boolean;
  selected?: boolean;
  justBoosted?: boolean;
  onOpen: () => void;
  onJoin: () => void;
  /** Right-click items, built by the list that owns the card. */
  menu?: () => MenuItem[];
};

export function PartyCard({ party, compact, row, selected, justBoosted, onOpen, onJoin, menu }: Props) {
  const hover = useHoverCard(() => <PartyHoverBody party={party} />, { width: 316 });
  const ctx = useContextMenu(() => menu?.() ?? []);
  const catalog = useSession((s) => s.catalog);
  const game = catalog?.games.find((g) => g.id === party.gameId);
  const me = useSession((s) => s.me);
  /* Why this one is in front of you - at most three, and only true ones. */
  const reasons = reasonsFor(party, me, game?.name);
  const ref = useRef<HTMLDivElement>(null);
  const [lit, setLit] = useState(false);

  const open = party.size - party.memberCount;
  const banner = gameBanner(party.gameId);
  const icon = gameIconArt(party.gameId);
  const regionName = catalog?.regions.find((r) => r.id === party.region)?.name ?? party.region;
  const host = party.members.find((m) => m.userId === party.hostId)?.user ?? null;

  if (row) {
    return (
      <div
        ref={ref}
        {...ctx.props}
        onClick={() => { hover.close(); onOpen(); }}
        style={{ ['--game' as string]: game?.color ?? 'var(--accent)' }}
        className={cn(
          'group relative isolate flex h-[68px] cursor-pointer items-center gap-3 overflow-hidden rounded-[calc(14px*var(--radius-mul,1))] border bg-surface-1 pr-2.5 pl-0',
          'transition-[border-color,background-color,box-shadow] duration-150',
          selected ? 'border-accent shadow-[0_0_0_1px_var(--accent)]' : 'border-line-soft hover:border-line'
        )}
      >
        {/* The game's banner, still: a strip on the left that fades into the
            row, so a list keeps the artwork without the height of a card. */}
        <span aria-hidden className="relative -z-10 h-full w-[150px] flex-none self-stretch overflow-hidden" style={{ background: 'color-mix(in srgb, var(--game) 22%, var(--bg-2))' }}>
          {banner ? <img src={banner} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" /> : null}
          <span className="absolute inset-0 bg-[linear-gradient(90deg,transparent_35%,var(--bg-1))]" />
          <span className="absolute bottom-1.5 left-2 grid h-7 w-7 place-items-center overflow-hidden rounded-[8px] bg-[rgba(0,0,0,.45)] text-white backdrop-blur-sm">
            {icon ? <img src={icon} alt="" loading="lazy" className="h-full w-full object-cover" /> : <Icon name={game?.icon ?? 'dice'} size={15} />}
          </span>
        </span>

        <div className="min-w-0 flex-[1.4]">
          <p className="m-0 truncate text-[14px] leading-tight font-bold text-ink"><EmojiText text={party.title} size={15} /></p>
          <p className="m-0 mt-0.5 truncate text-[12px] text-mute">
            {[game?.name ?? party.gameId, party.mode, party.minRank].filter(Boolean).join(' · ')}
          </p>
        </div>

        <div className="hidden min-w-0 flex-1 items-center gap-1.5 text-[12px] text-dim lg:flex">
          <Icon name="globe" size={13} className="flex-none text-mute" />
          <span className="truncate">{regionName}{party.language ? ' · ' + party.language : ''}</span>
          {party.micRequired ? <Icon name="mic" size={13} className="flex-none text-mute" /> : null}
        </div>

        <div className="flex flex-none items-center">
          {party.members.slice(0, 3).map((m, i) => m.user ? (
            <span key={m.userId} className="rounded-full ring-2 ring-surface-1" style={{ marginLeft: i ? -7 : 0 }}>
              <Avatar user={m.user} size={24} />
            </span>
          ) : null)}
        </div>

        <span className={cn('w-[46px] flex-none text-right text-[12.5px] font-semibold tabular-nums', open > 0 ? 'text-dim' : 'text-mute')}>
          {party.memberCount}/{party.size}
        </span>

        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); party.isMember ? onOpen() : onJoin(); }}
          disabled={!party.isMember && open <= 0}
          className={cn(
            'flex h-8 w-[84px] flex-none items-center justify-center gap-1 rounded-[calc(10px*var(--radius-mul,1))] text-[12.5px] font-bold transition-[background-color] duration-150 disabled:pointer-events-none disabled:opacity-40',
            party.isMember ? 'border border-line bg-surface-2 text-dim' : 'bg-accent text-accent-fg hover:bg-accent-hi'
          )}
        >
          {party.isMember ? 'Open' : open > 0 ? 'Join' : 'Full'}
        </button>
        {justBoosted ? <span aria-hidden className="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[0_0_0_2px_var(--accent)]" /> : null}
        {ctx.menu}
      </div>
    );
  }

  if (compact) {
    return (
      <div
        ref={ref}
        {...ctx.props}
        onClick={() => { hover.close(); onOpen(); }}
        style={{ ['--game' as string]: game?.color ?? 'var(--accent)' }}
        className={cn(
          'relative flex cursor-pointer flex-col gap-2.5 rounded-[var(--radius-l)] bg-surface-1 p-3',
          'border transition-[border-color,box-shadow] duration-150',
          selected
            ? 'border-accent shadow-[0_0_0_1px_var(--accent),0_0_18px_var(--glow)]'
            : 'border-line-soft'
        )}
      >
        <div className="flex items-start gap-3">
          {/* the artwork, as a square rather than a banner */}
          <span
            className="grid h-[62px] w-[62px] flex-none place-items-center overflow-hidden rounded-[calc(12px*var(--radius-mul,1))] border"
            style={{
              borderColor: 'color-mix(in srgb, var(--game) 34%, transparent)',
              background: 'color-mix(in srgb, var(--bg-0) 60%, transparent)',
              color: 'var(--game)'
            }}
          >
            {icon
              ? <img src={icon} alt="" loading="lazy" className="h-full w-full object-cover" />
              : <Icon name={game?.icon ?? 'dice'} size={30} />}
          </span>

          <div className="min-w-0 flex-1">
            <h3 className="truncate text-[14.5px] leading-tight font-bold uppercase">
              {game?.name ?? party.gameId}
            </h3>
            <p className="truncate text-[12px] text-mute">
              {party.mode}
              {party.minRank ? ' · ' + party.minRank : ''}
            </p>
            <p className="truncate text-[12px] text-dim"><EmojiText text={party.title} size={15} /></p>
          </div>

          {/* who is in it, on the right where the mockup puts them */}
          <div className="flex flex-none items-center">
            {party.members.slice(0, 3).map((m, i) => m.user ? (
              <span key={m.userId} className="rounded-full ring-2 ring-surface-1" style={{ marginLeft: i ? -8 : 0 }}>
                <Avatar user={m.user} size={26} />
              </span>
            ) : null)}
            {party.memberCount > 3 ? (
              <span
                className="grid h-[26px] min-w-[26px] place-items-center rounded-full bg-surface-3 px-1 text-[10px] font-bold text-dim ring-2 ring-surface-1"
                style={{ marginLeft: -8 }}
              >
                +{party.memberCount - 3}
              </span>
            ) : null}
          </div>
        </div>

        {host ? (
          <div className="flex items-center gap-2 text-[12px]">
            <Avatar user={host} size={20} presence />
            <span className="truncate font-semibold text-dim">{host.displayName}</span>
          </div>
        ) : null}

        <div className="flex flex-wrap gap-1.5">
          <Pill icon="globe">{regionName}</Pill>
          {party.language ? <Pill icon="translate">{party.language}</Pill> : null}
          {party.micRequired ? <Pill icon="mic">Mic</Pill> : null}
        </div>

        <div className="flex items-center gap-2 border-t border-line-soft pt-2.5">
          <span className="flex flex-none items-center gap-1.5 text-[12px] text-mute tabular-nums">
            <Icon name="users" size={13} className="opacity-70" />
            {party.memberCount} / {party.size}
          </span>
          {open > 0 ? (
            <span className="flex flex-none items-center gap-1 text-[12px] font-semibold text-good">
              <Icon name="mic" size={12} />
              Voice ready
            </span>
          ) : null}

          <span className="flex-1" />

          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); party.isMember ? onOpen() : onJoin(); }}
            disabled={!party.isMember && open <= 0}
            className={cn(
              'flex h-[36px] flex-none items-center justify-center gap-1.5 rounded-[var(--radius)] px-4 text-[13px] font-bold',
              'transition-[background-color] duration-150 disabled:pointer-events-none disabled:opacity-40',
              party.isMember
                ? 'border border-line bg-surface-2 text-dim'
                : 'bg-[linear-gradient(180deg,var(--accent-hi),var(--accent))] text-accent-fg shadow-[inset_0_1px_0_rgba(255,255,255,.18)]'
            )}
          >
            {party.isMember ? 'Open' : <><Icon name="bolt" size={13} />Quick Join</>}
          </button>

          <button
            type="button"
            aria-label="Save"
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
            className="grid h-[36px] w-[36px] flex-none place-items-center rounded-[var(--radius)] border border-line-soft bg-surface-2 text-mute"
          >
            <Icon name="bookmark" size={15} />
          </button>
        </div>

        {ctx.menu}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      {...ctx.props}
      onPointerEnter={(e) => { setLit(true); hover.props.onPointerEnter(e); }}
      onPointerLeave={(e) => { setLit(false); hover.props.onPointerLeave(e); }}
      onClick={() => { hover.close(); onOpen(); }}
      style={{ ['--game' as string]: game?.color ?? 'var(--accent)' }}
      className={cn(
        'group relative isolate flex cursor-pointer flex-col overflow-hidden rounded-[var(--radius-l)] bg-surface-1',
        'border transition-[border-color,box-shadow,background] duration-150',
        selected
          ? 'border-accent shadow-[0_0_0_1px_var(--accent),0_0_20px_var(--glow)]'
          : lit
            ? 'border-line bg-surface-2'
            : 'border-line-soft'
      )}
    >
      {/* the artwork */}
      <div className="relative h-[142px] flex-none overflow-hidden">
        {banner ? (
          <img
            src={banner}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <>
            <span
              className="absolute inset-0"
              style={{
                background:
                  'radial-gradient(90% 120% at 78% 18%, color-mix(in srgb, var(--game) 62%, transparent), transparent 66%),' +
                  'linear-gradient(125deg, color-mix(in srgb, var(--game) 42%, var(--art-base)), var(--art-base) 82%)'
              }}
            />
            <span className="absolute -right-5 -bottom-7 opacity-[.32] mix-blend-overlay" style={{ color: 'var(--game)' }}>
              <Icon name={game?.icon ?? 'dice'} size={150} stroke={.9} />
            </span>
          </>
        )}

        {/* Reads the art down into the card so the title never fights it. */}
        <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-surface-1 via-[color-mix(in_srgb,var(--bg-1)_30%,transparent)] to-[rgba(0,0,0,.35)]" />

        {/* At most two, and only the ones that change your mind. */}
        <div className="absolute top-2.5 left-2.5 flex flex-wrap gap-1.5">
          {party.micRequired ? <CardTag tone="voice">Mic required</CardTag> : null}
          {party.newcomers ? <CardTag tone="good">New players welcome</CardTag> : null}
          {party.sessionLength ? <CardTag>{LENGTH_LABEL[party.sessionLength]}</CardTag> : null}
          {party.boosted ? <CardTag tone="accent">Featured</CardTag> : null}
          {!party.micRequired && !party.boosted && open > 0 ? <CardTag tone="good">Voice ready</CardTag> : null}
        </div>

        <button
          type="button"
          aria-label="Save"
          onClick={(e) => { e.stopPropagation(); onOpen(); }}
          className="absolute top-2.5 right-2.5 grid h-7 w-7 place-items-center rounded-[calc(8px*var(--radius-mul,1))] bg-[rgba(0,0,0,.4)] text-white/70 opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100 hover:text-white"
        >
          <Icon name="bookmark" size={14} />
        </button>
      </div>

      <div className="flex flex-1 flex-col p-4">
        {/*
          Title first, game second.

          The old card led with the game name in capitals, which is the least
          distinguishing thing on a page that is already filtered by game - six
          cards reading COUNTER-STRIKE 2 tell you nothing about which to join.
          The reference leads with what the host wrote, and that is also the
          thing people actually scan for.
        */}
        <h3 className="m-0 truncate text-[15.5px] leading-tight font-bold tracking-[-.015em] text-ink">
          <EmojiText text={party.title} size={16} />
        </h3>
        <p className="mt-1 mb-0 line-clamp-2 min-h-[2.4em] text-[13px] leading-snug text-mute">
          {party.description || 'No description.'}
        </p>

        {/* Who is in it, how full it is, and whether you need a microphone. */}
        <div className="mt-3 flex items-center gap-2">
          <span className="flex items-center -space-x-2">
            {party.members
              .filter((m) => m.user)
              .slice(0, 3)
              .map((m) => (
                <span key={m.userId} className="rounded-full ring-2 ring-[var(--bg-1)]">
                  <Avatar user={m.user!} size={28} />
                </span>
              ))}
            {party.memberCount > 3 ? (
              <span className="grid h-[28px] w-[28px] place-items-center rounded-full bg-surface-3 text-[11px] font-bold text-dim ring-2 ring-[var(--bg-1)]">
                +{party.memberCount - 3}
              </span>
            ) : null}
          </span>

          <span className="ml-auto flex items-center gap-1.5 text-[12.5px] text-mute tabular-nums">
            <Icon name="users" size={14} className="opacity-70" />
            <span className="font-semibold text-dim">{party.memberCount} / {party.size}</span>
          </span>

          {party.micRequired ? (
            <Tooltip content="Voice chat required. You will need a microphone to join.">
              <span className="grid h-[28px] w-[28px] place-items-center rounded-full text-mute transition-[background-color,color] duration-[var(--t-hover)] ease-[var(--ease)] hover:bg-surface-3 hover:text-ink">
                <Icon name="mic" size={15} />
                <span className="sr-only">Voice chat required</span>
              </span>
            </Tooltip>
          ) : null}
        </div>

        {/* The requirements you scan, as chips rather than a run of icons. */}
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <Chip>{game?.name ?? party.gameId}</Chip>
          {party.mode ? <Chip>{party.mode}</Chip> : null}
          <Chip>{regionName}</Chip>
          {(party.platforms ?? []).length ? <Chip>{party.platforms!.map((p) => PLATFORM_LABEL[p] ?? p).join(' / ')}</Chip> : null}
          {party.tags?.slice(0, 1).map((t) => <Chip key={t}>{t}</Chip>)}
        </div>

        {/*
          Why this one is in front of you.

          A score out of a hundred is a number nobody believes. These are the
          reasons in the app's own words, so somebody can see the thinking and
          disagree with it.
        */}
        {reasons.length ? (
          <ul className="m-0 mt-2 flex list-none flex-wrap gap-1.5 p-0">
            {reasons.map((r) => (
              <li key={r.id} className="flex items-center gap-1.5 rounded-full bg-accent-soft px-2.5 py-[3px] text-[11.5px] font-semibold text-accent">
                <Icon name={r.icon} size={12} />
                {r.label}
              </li>
            ))}
          </ul>
        ) : null}

        {/*
          One action, and it never lies.

          A full party gets a disabled control that says it is full, rather
          than a Join that silently does nothing - the brief is explicit that
          a full party must never look freely joinable.
        */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); party.isMember ? onOpen() : onJoin(); }}
          disabled={!party.isMember && open <= 0}
          className={cn(
            'mt-4 flex h-[42px] w-full items-center justify-center gap-1.5 rounded-[calc(12px*var(--radius-mul,1))] text-[13.5px] font-bold',
            'transition-[background-color,border-color,color,transform] duration-[var(--t-hover)] ease-[var(--ease)]',
            'active:scale-[.99]',
            party.isMember
              ? 'border border-line bg-surface-2 text-dim hover:border-accent-line hover:text-ink'
              : open <= 0
                ? 'cursor-not-allowed border border-line bg-surface-2 text-ghost'
                : 'bg-accent text-accent-fg hover:bg-accent-hi'
          )}
        >
          {party.isMember ? 'Open party' : open <= 0 ? 'Party is full' : 'Join party'}
        </button>
        {/* A full party is not a dead end any more. */}
        {!party.isMember && open <= 0 ? (
          <div className="mt-2 flex justify-center" onClick={(e) => e.stopPropagation()}>
            <WaitButton party={party} />
          </div>
        ) : null}
      </div>

      {justBoosted ? (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 z-[1] rounded-[inherit] animate-[boost-sweep_1.1s_var(--ease-out-soft)]"
          style={{
            background:
              'linear-gradient(100deg, transparent 30%, color-mix(in srgb, var(--accent) 32%, transparent) 50%, transparent 70%)',
            backgroundSize: '260% 100%'
          }}
        />
      ) : null}

      {hover.card}
      {ctx.menu}
    </div>
  );
}

/** A small neutral label. Used for the facts that narrow a choice. */
function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-full bg-surface-3 px-2.5 py-[3px] text-[11.5px] font-semibold text-dim capitalize">
      {children}
    </span>
  );
}

/** A tag over the artwork. Dark enough to stay readable on any banner. */
/** How a party's session length reads on a card. */
export const LENGTH_LABEL: Record<string, string> = {
  one: 'One match',
  short: '~1 hour',
  long: 'Long session'
};

function CardTag({ tone, children }: { tone?: 'good' | 'voice' | 'accent'; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        'rounded-[calc(6px*var(--radius-mul,1))] px-2 py-0.5 text-[10.5px] font-bold backdrop-blur-sm',
        tone === 'good' && 'bg-[color-mix(in_srgb,var(--good)_20%,rgba(0,0,0,.55))] text-good',
        tone === 'voice' && 'bg-[color-mix(in_srgb,var(--voice)_20%,rgba(0,0,0,.55))] text-voice',
        tone === 'accent' && 'bg-[color-mix(in_srgb,var(--accent)_45%,rgba(0,0,0,.35))] text-white',
        !tone && 'bg-[rgba(0,0,0,.5)] text-white/85'
      )}
    >
      {children}
    </span>
  );
}

/** A requirement pill on the phone card. */
function Pill({ icon, children }: { icon: string; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-line-soft bg-surface-2 px-2.5 py-1 text-[11.5px] font-semibold text-mute">
      <Icon name={icon} size={11} className="flex-none opacity-70" />
      {children}
    </span>
  );
}